import { Flex, Menu, MenuButton, MenuItem, MenuList, useToast } from '@chakra-ui/react';
import { Permission, ServiceAdminRole } from '@tp-vision/roles-permissions';
import { t } from 'i18next';
import { Trans } from 'react-i18next';
import { CellProps } from 'react-table';
import { useAuth } from '~auth/useAuth';
import { ActionMenuButton } from '~components/ui/ActionMenuButton';
import { useDestructiveAction } from '~components/ui/DestructiveAction';
import { EditIcon, RevokeIcon } from '~components/ui/icons';
import { StopClickPropagation } from '~components/ui/StopClickPropagation';
import { fromError } from '~utils/errors';
import { AdminProfilesTable_UserFragment } from './__generated__/useAdminProfilesTable.graphql';
import { getAdminRolesErrorMap } from './adminRolesErrorMap';
import { getServiceAdminRoleLabel } from './serviceAdminRoleCategories';
import { useUpdateAdminRoles } from './UpdateAdminRoles';

interface Props {
  onEdit: (user: AdminProfilesTable_UserFragment) => void;
  onRevoked: () => void;
}

export function AdminProfilesActionsCell({
  row: { original: user },
  onEdit,
  onRevoked,
}: CellProps<AdminProfilesTable_UserFragment> & Props) {
  const toast = useToast();
  const { verifyUserPermissions } = useAuth();
  const canGrantSuperAdmin = verifyUserPermissions([Permission.SuperAdminGrant]);
  const [updateAdminRoles] = useUpdateAdminRoles();

  const revokeAction = useDestructiveAction<AdminProfilesTable_UserFragment>({
    title: t('revokeAdminAccess'),
    message: (target) => (
      <span
        dangerouslySetInnerHTML={{
          __html: t('revokeAdminAccessConfirm', {
            name: `${target.givenName} ${target.familyName}`,
            roles: target.serviceAdminRoles.map(getServiceAdminRoleLabel).join(', '),
          }),
        }}
      />
    ),
    confirmLabel: t('revoke'),
    onConfirm: async (target) => {
      try {
        await updateAdminRoles(target, []);
        toast({
          status: 'success',
          title: (
            <Trans
              i18nKey="adminRevokeSuccess"
              values={{ name: `${target.givenName} ${target.familyName}` }}
              components={{ strong: <strong /> }}
            />
          ),
        });
        onRevoked();
      } catch (err) {
        toast({
          status: 'error',
          title: t('adminRevokeError'),
          description: fromError(err, 'UpdateAdminRoles', getAdminRolesErrorMap()),
        });
      }
    },
  });

  const isSuperAdmin = user.serviceAdminRoles.includes(ServiceAdminRole.SuperAdmin);

  return (
    <>
      <StopClickPropagation>
        <Flex justifyContent="center">
          <Menu placement="bottom-end">
            <MenuButton as={ActionMenuButton} />
            <MenuList>
              <MenuItem icon={<EditIcon />} onClick={() => onEdit(user)}>
                {t('editAdminRoles')}
              </MenuItem>
              <MenuItem
                color="red.500"
                icon={<RevokeIcon color="red.500" />}
                onClick={() => revokeAction.askConfirmation(user)}
                isDisabled={isSuperAdmin && !canGrantSuperAdmin}
              >
                {t('revokeAdminAccess')}
              </MenuItem>
            </MenuList>
          </Menu>
        </Flex>
      </StopClickPropagation>
      {revokeAction.confirmationNode}
    </>
  );
}
